import type { OutboxEntry } from '../lib/outbox';
import './CaptureReceipt.css';

// S5c §1 — THE RECEIPT, AND THE ONE THING IT MUST NEVER DO.
//
// wf03 frame 3 ("Capture, offline"), annotated: *"The receipt is honest about
// what has and has not happened."* Two states, and the difference is the whole
// feature:
//
//   SOLID   — what has actually happened ON THIS DEVICE. The words were heard
//             and kept; the bytes are here. Both are true with the radio off.
//   PENDING — what has NOT happened yet, in ink (never amber, never red),
//             because the queue sends itself and there is nothing to chase.
//
// IT NEVER CLAIMS A CASCADE IT HAS NOT RUN. A receipt that lists a visit as
// created when no server has seen it is a lie the tech discovers at the worst
// possible moment.
//
// WHERE THIS DEPARTS FROM THE DRAWING, AND WHY. wf03 draws three pending
// lines — Visit · Material line · Payout. Today's capture pipeline
// (packages/server/src/plugins/actorCapture.ts) does not derive those: it
// stages ONE deterministic proposal — attach to what you were viewing, note on
// it, or keep it in the inbox. Printing "Visit — when you have signal" would
// therefore be claiming a cascade that does not exist even when the tech has
// five bars, which is the exact failure the frame's own annotation forbids.
// So the pending line names the write that is genuinely outstanding. The three
// drawn lines become true the day the pack derives them, and this component is
// where they land — reported as a gap rather than mimed.
//
// AND BECAUSE NOTHING WAS DERIVED, IT IS FREELY EDITABLE. There is no return
// window until there is something to return, which is why "Edit what I said"
// is offered here and nowhere else in the product.
export interface CaptureReceiptProps {
  entry: OutboxEntry;
  /** How many entries sit AHEAD of this one. Order is a promise this queue
   *  keeps, so it is a fact worth showing rather than hiding. */
  ahead: number;
  onEdit: () => void;
  onDone: () => void;
}

export function CaptureReceipt({ entry, ahead, onEdit, onDone }: CaptureReceiptProps) {
  const said = entry.text.trim();
  const stored = storedLine(entry.files);
  // S5d §1 — a queued VOICE capture has no transcript, because transcription
  // is server-side and no server has seen this. The words line says what is
  // true instead of inventing a quote: the words are in the recording, and
  // they are heard when it sends.
  const spokenOnly =
    said === '' && entry.files.some((f) => (f.type.split('/')[0] || '') === 'audio');

  return (
    <div class="capture-receipt" role="status">
      <div class="capture-receipt__head">Captured</div>

      <div class="capture-receipt__card">
        {said ? (
          <div class="capture-receipt__said">
            <span class="capture-receipt__dot" aria-hidden="true" />
            <em>“{said}”</em>
          </div>
        ) : spokenOnly ? (
          <div class="capture-receipt__said">
            <span class="capture-receipt__dot" aria-hidden="true" />
            <em>Your words are in the recording — heard when it sends.</em>
          </div>
        ) : null}

        <div class="capture-receipt__row">
          <b>Heard and kept</b>
          <span>{clock(entry.createdAt)}</span>
        </div>

        {stored ? (
          <div class="capture-receipt__row">
            <b>{stored}</b>
            <span>on this phone</span>
          </div>
        ) : null}

        <div class="capture-receipt__row capture-receipt__row--pending">
          {/* "Handed over", not "filed". The queue's one call is the hand-over
              (POST /api/actor/capture); what is made of it afterwards follows
              the ordinary review path, exactly as it does for a capture made
              with five bars. A real walk proved the difference matters: the
              drain used to approve too, and the server answered 403 — a tech
              may not approve. See lib/outbox.ts's header. */}
          <b>Handed over</b>
          <span>when you have signal</span>
        </div>

        {ahead > 0 ? (
          <div class="capture-receipt__row capture-receipt__row--pending">
            <b>In line</b>
            <span>after {ahead} earlier</span>
          </div>
        ) : null}
      </div>

      <p class="capture-receipt__promise">
        Nothing is lost. You can change any of this until it sends.
      </p>

      <div class="capture-receipt__acts">
        <button type="button" class="capture-receipt__btn" onClick={onEdit}>
          Edit what I said
        </button>
        <button
          type="button"
          class="capture-receipt__btn capture-receipt__btn--quiet"
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/** What is actually on the phone, named by what it IS. wf03 says "Audio
 *  stored"; a photo is not audio and saying so would be the same kind of small
 *  untruth this component exists to avoid. Since S5e the entry holds bytes,
 *  not Blobs, which is what makes "on this phone" literally true on WebKit —
 *  the platform techs actually carry (see lib/outbox.ts's header). */
function storedLine(files: Array<{ type: string }>): string | null {
  if (files.length === 0) return null;
  const kinds = new Set(files.map((f) => (f.type.split('/')[0] || 'file')));
  if (files.length === 1 && kinds.has('audio')) return 'Audio stored';
  if (files.length === 1 && kinds.has('image')) return 'Photo stored';
  if (files.length === 1) return 'File stored';
  if (kinds.size === 1 && kinds.has('audio')) return `${files.length} recordings stored`;
  if (kinds.size === 1 && kinds.has('image')) return `${files.length} photos stored`;
  return `${files.length} files stored`;
}

function clock(at: number): string {
  const d = new Date(at);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}
